import { encodePath } from './pathEncoder.js';
import { GLOBAL_TAG } from './TOKENS.js';
import {
  MARKER_GLOBAL,
  MARKER_SINGLE,
  MARKER_MANY,
  MARKER_EPHEMERAL,
  MATCH_EXACT,
  MATCH_PARENT,
  MATCH_CHILD,
  MATCH_NONE 
} from './TOKENS.js';

// Marker type bitflags

// Helper functions to check marker type
export function isGlobalMarker(marker) {
  return (marker.type & MARKER_GLOBAL) === MARKER_GLOBAL;
}

export function isPathMarker(marker) {
  return (marker.type & MARKER_SINGLE) === MARKER_SINGLE;
}

export function isMarkers(marker) {
  return (marker.type & MARKER_MANY) === MARKER_MANY;
}

export function isEphemeralMarker(marker) {
  return (marker.type & MARKER_EPHEMERAL) === MARKER_EPHEMERAL;
}

export function isGlobalPath(path) {
  return !path || path.length === 0 || path === GLOBAL_TAG || path[0] === GLOBAL_TAG;
}

export function normalizePaths(args = []) {  
  const len = args.length;
  if (len === 0) return [0, [] ];
  if (len === 1 && args[0] === GLOBAL_TAG) return [0, [] ];
  
  if (Array.isArray(args[0])) {
    return len === 1 
      ? [1, [args[0]] ]
      : [len, args ];
  }
  
  return [1, [[...args]] ];
}

function pathHasEphemeral(path) {
  if (!Array.isArray(path)) {
    return false;
  }

  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (typeof segment === 'string' && segment.charCodeAt(0) === 46) { // '.'
      return true;
    }
  }

  return false;
}

// Marker factory
export function createMarker(paths = []) {
  if (isGlobalPath(paths)) {
    return {
      address: GLOBAL_TAG,
      isMarker: true,
      length: 0,
      path: [],
      children: null,
      type: MARKER_GLOBAL
    };
  }
  
  const [length, normalizedPaths] = normalizePaths(paths);
  const type = length === 1 ? MARKER_SINGLE : MARKER_MANY;
  const path = length === 1 ? normalizedPaths[0] : normalizedPaths;
  const children = length > 1 ? normalizedPaths.map(path => createMarker(path)) : null;
  const isEphemeral = type === MARKER_SINGLE
    ? pathHasEphemeral(path)
    : children.some(child => isEphemeralMarker(child));

  let markerType = type;
  if (isEphemeral) {
    markerType |= MARKER_EPHEMERAL;
  }

  return {
    address: type === MARKER_SINGLE ? encodePath(path) : null,
    isMarker: true,
    length,
    path,
    children,
    type: markerType
  };
}

export function createChildMarker(parentMarker, childPaths) {
  if (childPaths.length === 0) {
    return parentMarker;
  }
  // Normalize the child paths
  const [childLength, normalizedChildPaths] = normalizePaths(childPaths);
  
  // Handle global marker - just return marker with child paths
  if (isGlobalMarker(parentMarker)) {
    return createMarker(normalizedChildPaths, parentMarker.state);
  }
  
  // Handle single path marker
  if (isPathMarker(parentMarker)) {
    if (childLength === 0) {
      // No child paths, return parent as-is
      return parentMarker;
    }
    
    if (childLength === 1) {
      // Single child path - append to parent path
      const newPath = [...parentMarker.path, ...normalizedChildPaths[0]];
      return createMarker(newPath, parentMarker.state);
    } else {
      // Multiple child paths - create many markers
      const newPaths = normalizedChildPaths.map(childPath => 
        [...parentMarker.path, ...childPath]
      );
      return createMarker(newPaths, parentMarker.state);
    }
  }
  
  // Handle many markers - recursively create child markers for each
  if (isMarkers(parentMarker)) {
    const newMarkers = new Array(parentMarker.length);
    let i = 0;
    while (i < parentMarker.length) {
      newMarkers[i] = createChildMarker(parentMarker.children[i], childPaths);
      i++;
    }
    
    // Collect all paths from the new markers
    const allPaths = [];
    i = 0;
    while (i < newMarkers.length) {
      const marker = newMarkers[i];
      if (isPathMarker(marker)) {
        allPaths.push(marker.path);
      } else if (isMarkers(marker)) {
        let j = 0;
        while (j < marker.length) {
          allPaths.push(marker.children[j].path);
          j++;
        }
      }
      i++;
    }
    
    return createMarker(allPaths, parentMarker.state);
  }
  
  // Fallback - shouldn't reach here
  return parentMarker;
}

export function dispatch(marker, { global, path, ephemeral, error }) {
  try {
    if (!marker.isMarker) return undefined;
    if (isGlobalMarker(marker)) return global ? global(marker) : undefined;
    if (isEphemeralMarker(marker)) return ephemeral ? ephemeral(marker) : path ? path(marker) : undefined;
    if (isPathMarker(marker)) return path ? path(marker) : undefined;
    if (isMarkers(marker)) {
      const results = new Array(marker.length);
      let i = 0;
      while (i < marker.length) {
        const nestedMarker = marker.children[i];
        results[i] = dispatch(nestedMarker, { global, path, ephemeral, error });
        i++;
      }
      return results;
    }
    
    return undefined;
  } catch (err) {
    return error ? error(err.message) : undefined;
  }
}

export function compareMarkers(controlMarker, comparedMarker) {
  // Both are global markers or exact address match
  if (isGlobalMarker(controlMarker) && isGlobalMarker(comparedMarker)) {
    return MATCH_EXACT;
  }

  // If comparedMarker is global, it's always a parent
  if (isGlobalMarker(comparedMarker)) {
    return MATCH_PARENT;
  }
  
  // Need addresses to compare
  if (!controlMarker.address || !comparedMarker.address) {
    return MATCH_NONE;
  }
  
  // Exact match
  if (controlMarker.address === comparedMarker.address) {
    return MATCH_EXACT;
  }
  
  // controlMarker is more nested (child) - parent changed
  if (controlMarker.address.startsWith(comparedMarker.address + '.')) {
    return MATCH_PARENT;
  }
  
  // comparedMarker is more nested (child) - child changed
  if (comparedMarker.address.startsWith(controlMarker.address + '.')) {
    return MATCH_CHILD;
  }
  
  return MATCH_NONE;
}

export const Marker = {
  compare: compareMarkers,
  create: createMarker,
  createChild: createChildMarker,
  dispatch: dispatch,
  isGlobal: isGlobalMarker,
  isSingle: isPathMarker,
  isMarkers: isMarkers,
  isEphemeral: isEphemeralMarker
};